import { createContext, useContext, useEffect, useState } from "react";
import { privacyContent } from "@/content/privacy";

const LangCtx = createContext(null);

const translations = {
  en: {
    nav: { tours: "Tours", about: "About", gallery: "Gallery", stories: "Stories", contact: "Contact", book: "Book a tour" },
    hero: {
      overline: "Official certified guide · Sultanate of Oman",
      title: "Explore Oman with Khalfan, an official local guide",
      lede: "Private tours in Muscat, wadis, mountains and desert — simple, flexible and authentic.",
      sub: "",
      cta_primary: "View tours",
      cta_whatsapp: "Message on WhatsApp",
    },
    hero_reassurance: {
      items: [
        { title: "Official Certified Guide", body: "Licensed by the Government of Oman" },
        { title: "Easy WhatsApp Planning", body: "Ask questions and plan directly" },
        { title: "Follow the Journey", body: "See real moments on Instagram" },
        { title: "Private Flexible Tours", body: "No large groups, no agency feeling" },
      ],
    },
    trust: {
      overline: "Why travel with Khalfan",
      title: "An official guide with local knowledge.",
      sub: "Certified by the Government of Oman, easy to contact, and focused on private tours that feel human and flexible.",
      points: [
        { title: "Official certified guide", body: "Licensed by the Government of Oman and born and raised here." },
        { title: "Hotel pickup included", body: "Free pickup from any hotel or address in Muscat. Other cities on request." },
        { title: "English", body: "Fluent in English. Arabic native." },
        { title: "Flexible itinerary", body: "Want to stop for photos, swim longer, or change plans? The route can adapt." },
        { title: "WhatsApp support", body: "Direct WhatsApp before, during and after your trip. Same-day replies." },
      ],
    },
    process: {
      overline: "Booking is simple",
      title: "How it works",
      steps: [
        { n: "01", title: "Choose a tour", body: "Pick the experience and a date that works for you." },
        { n: "02", title: "Send a request", body: "Fill in a short form — no payment required upfront." },
        { n: "03", title: "Khalfan confirms", body: "You'll hear back on WhatsApp within hours, in English." },
        { n: "04", title: "Pay securely", body: "Pay via a Stripe or PayPal link sent by Khalfan, only once confirmed." },
      ],
    },
    card: {
      view: "View tour",
    },
    home: {
      tours_overline: "Popular tours",
      tours_title: "Three ways to discover Oman",
      tours_sub: "Each tour is private and flexible. Prices are per group, never per person.",
      view_all: "View all tours",
      about_overline: "Your guide",
      about_title: "Meet Khalfan",
      about_more: "More about me",
      testimonials_overline: "From recent guests",
      testimonials_title: "What travelers say",
      gallery_overline: "Real moments",
      gallery_title: "Oman, slowly",
      see_gallery: "See full gallery",
      stories_overline: "Notes from the road",
      stories_title: "Stories & travel notes",
      faq_overline: "Practical",
      faq_title: "Frequently asked",
      cta_title: "Ready to plan your trip?",
      cta_sub: "Send a quick message — I reply on WhatsApp the same day, in English.",
      from: "from",
    },
    tour: {
      overview: "Overview",
      includes: "What's included",
      select_date: "Choose an available date",
      no_dates: "No public dates listed. Message me on WhatsApp for custom dates.",
      book_form: "Booking request",
      book_btn: "Send booking request",
      booked_title: "Booking request sent",
      booked_sub: "Thank you — I will reply on WhatsApp shortly.",
      back: "Back to all tours",
      per_group: "per group",
      trust_title: "Why book with Khalfan?",
      trust_items: [
        "Official certified guide",
        "Flexible private itinerary",
        "WhatsApp support before the tour",
        "Payment only after confirmation",
      ],
    },
    form: {
      name: "Your name",
      email: "Email",
      whatsapp: "WhatsApp number",
      guests: "Number of travelers",
      message: "Message (optional)",
      date: "Date",
      tour: "Tour",
      reassurance: "You are contacting an official certified Omani guide. Khalfan will personally confirm your request before payment.",
      wait: "Please wait a moment before sending.",
    },
    gallery_page: { title: "Gallery", sub: "Photographs from the road, taken on tour." },
    about_page: { title: "About Khalfan", overline: "Official certified Omani guide" },
    blog_page: { title: "Stories & notes", sub: "Practical guides and small stories from my travels across Oman.", read: "Read" },
    contact_page: {
      title: "Get in touch",
      sub: "WhatsApp is the fastest way to reach me. You can also send a request below.",
      form_title: "Send a message",
      sent: "Message sent — I'll reply soon.",
    },
    footer: { rights: "All rights reserved", made: "Official certified guide in the Sultanate of Oman", privacy: "Privacy & Cookies", cookie_preferences: "Cookie preferences" },
    privacy_page: privacyContent.en,
    cookie_consent: {
      overline: "Privacy",
      title: "Your privacy choices",
      description: "We use analytics cookies to understand how visitors use the website and improve the experience. Analytics stays off unless you accept. We do not use advertising cookies.",
      accept: "Accept analytics",
      reject: "Reject analytics",
    },
    admin: { login: "Admin login", email: "Email", password: "Password", signin: "Sign in", logout: "Sign out" },
  },
  fr: {
    nav: { tours: "Voyages", about: "À propos", gallery: "Galerie", stories: "Récits", contact: "Contact", book: "Réserver" },
    hero: {
      overline: "Guide officiel certifié · Sultanat d'Oman",
      title: "Explorez Oman avec Khalfan, guide local officiel",
      lede: "Voyages privés à Mascate, dans les wadis, les montagnes et le désert — simples, flexibles et authentiques.",
      sub: "",
      cta_primary: "Voir les voyages",
      cta_whatsapp: "Message sur WhatsApp",
    },
    hero_reassurance: {
      items: [
        { title: "Guide officiel certifié", body: "Agréé par le gouvernement d'Oman" },
        { title: "Planification simple sur WhatsApp", body: "Posez vos questions et organisez directement" },
        { title: "Suivez le voyage", body: "Voir de vrais moments sur Instagram" },
        { title: "Voyages privés et flexibles", body: "Pas de grands groupes, pas d'esprit agence" },
      ],
    },
    trust: {
      overline: "Pourquoi voyager avec Khalfan",
      title: "Un guide officiel avec une vraie connaissance locale.",
      sub: "Certifié par le gouvernement d'Oman, facile à contacter, avec des voyages privés simples et flexibles.",
      points: [
        { title: "Guide officiel certifié", body: "Agréé par le gouvernement d'Oman, né et élevé ici." },
        { title: "Prise en charge à l'hôtel", body: "Pickup gratuit depuis votre hôtel à Mascate. Autres villes sur demande." },
        { title: "Anglais", body: "Anglais courant. Arabe natif." },
        { title: "Itinéraire flexible", body: "Envie de vous arrêter pour des photos ou nager plus longtemps ? Le programme peut s'adapter." },
        { title: "Support WhatsApp", body: "WhatsApp direct avant, pendant et après votre voyage. Réponse le jour même." },
      ],
    },
    process: {
      overline: "La réservation est simple",
      title: "Comment ça marche",
      steps: [
        { n: "01", title: "Choisissez un tour", body: "Sélectionnez l'expérience et une date qui vous convient." },
        { n: "02", title: "Envoyez une demande", body: "Remplissez un court formulaire — aucun paiement à l'avance." },
        { n: "03", title: "Khalfan confirme", body: "Vous recevez une réponse sur WhatsApp en quelques heures." },
        { n: "04", title: "Payez en toute sécurité", body: "Réglez via un lien Stripe ou PayPal, uniquement une fois confirmé." },
      ],
    },
    card: {
      view: "Voir le voyage",
    },
    home: {
      tours_overline: "Voyages populaires",
      tours_title: "Trois façons de découvrir Oman",
      tours_sub: "Chaque voyage est privé et flexible. Les prix sont par groupe, jamais par personne.",
      view_all: "Voir tous les voyages",
      about_overline: "Votre guide",
      about_title: "Rencontrez Khalfan",
      about_more: "En savoir plus",
      testimonials_overline: "Voyageurs récents",
      testimonials_title: "Ce qu'ils en disent",
      gallery_overline: "Vrais moments",
      gallery_title: "Oman, doucement",
      see_gallery: "Voir la galerie",
      stories_overline: "Notes de voyage",
      stories_title: "Récits & carnets",
      faq_overline: "Pratique",
      faq_title: "Questions fréquentes",
      cta_title: "Prêt à planifier votre\u00a0voyage\u00a0?",
      cta_sub: "Envoyez un message — je réponds le jour même sur WhatsApp, en anglais.",
      from: "à partir de",
    },
    tour: {
      overview: "Aperçu",
      includes: "Inclus",
      select_date: "Choisissez une date disponible",
      no_dates: "Aucune date publiée. Contactez-moi sur WhatsApp pour des dates personnalisées.",
      book_form: "Demande de réservation",
      book_btn: "Envoyer la demande",
      booked_title: "Demande envoyée",
      booked_sub: "Merci — je vous réponds sur WhatsApp très vite.",
      back: "Retour aux voyages",
      per_group: "par groupe",
      trust_title: "Pourquoi réserver avec Khalfan ?",
      trust_items: [
        "Guide officiel certifié",
        "Itinéraire privé et flexible",
        "Support WhatsApp avant le tour",
        "Paiement uniquement après confirmation",
      ],
    },
    form: {
      name: "Votre nom",
      email: "Email",
      whatsapp: "Numéro WhatsApp",
      guests: "Nombre de voyageurs",
      message: "Message (optionnel)",
      date: "Date",
      tour: "Voyage",
      reassurance: "Vous contactez un guide omanais officiel certifié. Khalfan confirmera personnellement votre demande avant le paiement.",
      wait: "Merci d'attendre un instant avant d'envoyer.",
    },
    gallery_page: { title: "Galerie", sub: "Photographies prises pendant mes voyages." },
    about_page: { title: "À propos de Khalfan", overline: "Guide omanais officiel certifié" },
    blog_page: { title: "Récits & notes", sub: "Guides pratiques et petites histoires de mes voyages à travers Oman.", read: "Lire" },
    contact_page: {
      title: "Me contacter",
      sub: "WhatsApp est le moyen le plus rapide. Vous pouvez aussi envoyer une demande ci-dessous.",
      form_title: "Envoyer un message",
      sent: "Message envoyé — je vous réponds bientôt.",
    },
    footer: { rights: "Tous droits réservés", made: "Guide officiel certifié au Sultanat d'Oman", privacy: "Confidentialité et cookies", cookie_preferences: "Préférences des cookies" },
    privacy_page: privacyContent.fr,
    cookie_consent: {
      overline: "Confidentialité",
      title: "Vos choix de confidentialité",
      description: "Nous utilisons des cookies d’analyse pour comprendre comment le site est utilisé et améliorer l’expérience. Les statistiques restent désactivées tant que vous ne les acceptez pas. Nous n’utilisons pas de cookies publicitaires.",
      accept: "Accepter les statistiques",
      reject: "Refuser les statistiques",
    },
    admin: { login: "Connexion admin", email: "Email", password: "Mot de passe", signin: "Connexion", logout: "Déconnexion" },
  },
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("ko_lang") || "en");
  useEffect(() => {
    localStorage.setItem("ko_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = translations[lang] || translations.en;
  const pick = (en, fr) => (lang === "fr" ? fr || en : en);
  return (
    <LangCtx.Provider value={{ lang, setLang, t, pick }}>{children}</LangCtx.Provider>
  );
}

export const useLang = () => useContext(LangCtx);
